import React from 'react';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { getActiveLocaleSelector } from '@americanexpress/travel-selectors';
import { formatPointsWithLocale } from '@americanexpress/consumer-travel-base';
import { compose } from 'redux';
import { withConditionalFeatures } from '@americanexpress/conditional-features';
import clsx from 'clsx';
import { Accordion, Heading, Surface } from '@americanexpress/dls-react';
import Benefit from './Benefit';
import { getCardArt, getTravelerPoints } from '../../utils/travelerDataUtils';
import TravelCreditTabButtonWrapper from '../TravelCreditTab/TravelCreditTabButtonWrapper';
import { hasDisableCardSpecificBenefitsRule } from '../../constants/featureRulesConstants';
import styles from './TravelBenefits.scss';
import { MODULE_NAME } from '../../constants/modules';

const TravelBenefits = ({
  travelBenefits,
  travelerData,
  selectedAccountData,
  hasDisableCardSpecificBenefits,
}) => {
  const { formatMessage } = useIntl();
  const locale = useSelector(getActiveLocaleSelector);
  const memberPoints = formatPointsWithLocale(
    getTravelerPoints(travelerData),
    locale
  );
  const cardArt = getCardArt(travelerData);
  const hideCardArtChecklist = ['', undefined, null];
  const showCardSpecificBenefits = !hasDisableCardSpecificBenefits;
  const showBottomBorder = !hasDisableCardSpecificBenefits;

  if (!travelBenefits) return null;

  return (
    <section className={clsx(!showBottomBorder && 'border-b')}>
      <Surface variant="base" className={styles.radiusContainer}>
        <div className="border-t pad-2-lr pad-2-b text-align-center">
          {!hideCardArtChecklist.includes(cardArt) && (
            <img
              src={cardArt}
              alt={formatMessage({ id: 'cardArt' })}
              className={styles.cardArt}
            />
          )}
          <Heading id="my-travel-benefits-banner" level={2} variant="sans-xsmall-bold">
            {formatMessage({ id: 'travelBenefits.heading' })}
          </Heading>
          {memberPoints && (
            <div className="legal-1 pad-2-t">
              {/* eslint-disable-next-line react/no-danger -- data from oneCMS */}
              <p dangerouslySetInnerHTML={{
                __html: formatMessage({ id: 'travelBenefits.rewardsBrand' }),
              }}
              />
              <span className="heading-4">
                {`${memberPoints} ${formatMessage({ id: 'points' })}`}
              </span>
            </div>
          )}
        </div>
        <TravelCreditTabButtonWrapper selectedAccountData={selectedAccountData} />
        {showCardSpecificBenefits && (
          <div data-dls-surface="foreground" className="flex-row">
            <Accordion
              shouldAllowMultipleExpanded={false}
              data-testid={`${MODULE_NAME}-benefits`}
            >
              {Object.values(travelBenefits).map((benefit, index) => {
                const analyticsDetail = `hp-benefits${index + 1}`;
                return (
                  <Benefit
                    key={analyticsDetail}
                    analyticsDetail={analyticsDetail}
                    benefit={benefit}
                  />
                );
              })}
            </Accordion>
          </div>
        )}
      </Surface>
    </section>
  );
};

TravelBenefits.propTypes = {
  travelBenefits: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  travelerData: PropTypes.shape({
    card: PropTypes.shape({
      cardArtCdnLink: PropTypes.string,
    }),
  }).isRequired,
  selectedAccountData: PropTypes.shape({
    displayAccountNumber: PropTypes.string,
    cardArtCdnLink: PropTypes.string,
    travelCredit: PropTypes.shape({
      name: PropTypes.string,
      description: PropTypes.string,
      expiry: PropTypes.string,
    }),
  }),
  hasDisableCardSpecificBenefits: PropTypes.bool,
};

const mapFeaturesToProps = {
  hasDisableCardSpecificBenefits: hasDisableCardSpecificBenefitsRule,
};

const hocChain = compose(withConditionalFeatures({ mapFeaturesToProps }));

export { TravelBenefits as TestableTravelBenefits };

export default hocChain(TravelBenefits);
